<template>
    <BaseDialog v-model:open="open" title="Share your Profile" description="Let colleagues know how to work with you" :show-close="false">
        <div class="flex flex-col p-6 md:w-120">
            <!-- Header -->
            <div class="flex justify-between items-start">
                <div>
                    <h2 class="display-serif text-valence-grey-850">Share your Profile</h2>
                    <p class="body-small text-valence-grey-750 mt-1">Let colleagues know how to work with you</p>
                </div>
                <DialogClose class="p-2 bi bi-x-lg text-base/none icon-semibold text-valence-grey-750 cursor-pointer shrink-0" aria-label="Close dialog" />
            </div>

            <HorizontalDivider />

            <!-- General Access -->
            <div class="flex items-center justify-between" data-testid="general-access-section">
                <div class="flex items-center gap-2">
                    <span class="body-semibold text-valence-grey-850">General Access</span>
                    <LoadingSpinner v-if="isAccessChanging" class="text-valence-grey-750 w-4 h-4" />
                    <i v-else-if="isShowingAccepted" class="bi bi-check text-green-600 text-xl/none" aria-hidden="true"></i>
                </div>
                <BaseSelect
                    v-model="localAccess"
                    :disabled="isButtonDisabled"
                    :options="accessOptions"
                    trigger-class="w-auto rounded-lg border border-valence-grey-300 bg-white px-4 py-2 body-small text-valence-grey-850"
                    @update:model-value="handleChangeProfileAccess" />
            </div>

            <HorizontalDivider />

            <div class="md:h-80 md:overflow-y-auto">
                <div class="flex flex-col md:h-full">
                    <template v-if="isOrgAccessLevel || isInviteOnlyAccessLevel">
                        <!-- Organization -->
                        <div v-if="isOrgAccessLevel" class="flex gap-3 rounded-lgr bg-card-surface-raised p-4" data-testid="organization-access-message">
                            <span class="grid size-11 shrink-0 place-items-center rounded-xl bg-valence-primary-500 text-white">
                                <OrganizationIcon class="h-6" aria-hidden="true" />
                            </span>
                            <div class="min-w-0">
                                <p class="body-small-bold text-valence-grey-850">Visible to your organization</p>
                                <p class="body-small text-valence-grey-750 mt-0.5">Everyone in your organization can find your profile and see how you work best.</p>
                            </div>
                        </div>

                        <!-- People with access (invite-only) -->
                        <template v-else>
                            <div v-if="showInvitationSkeletons" class="flex flex-col gap-4 grow">
                                <h3 class="body-bold text-valence-grey-750 mb-1">People with access</h3>
                                <div v-for="n in 2" :key="n" class="flex items-center gap-3">
                                    <SkeletonLoader class="size-10 shrink-0 rounded-full" aria-label="Loading user" />
                                    <div class="flex-1 flex flex-col gap-1.5">
                                        <SkeletonLoader class="h-3.5 w-48 rounded" aria-label="Loading name" />
                                        <SkeletonLoader class="h-3 w-40 rounded" aria-label="Loading status" />
                                    </div>
                                </div>
                            </div>
                            <div
                                v-else-if="activeAcceptances.length === 0"
                                class="flex flex-col grow justify-center items-center text-center py-2 gap-2"
                                data-testid="no-acceptances-message">
                                <i class="bi bi-people text-2xl text-valence-grey-400" aria-hidden="true"></i>
                                <p class="body-small text-valence-grey-500">No one has accepted your invite yet. Share your link to get started!</p>
                            </div>
                            <div v-else class="grow">
                                <h3 class="body-bold text-valence-grey-750 mb-3">People with access</h3>
                                <div class="flex flex-col gap-4">
                                    <div v-for="acceptance in activeAcceptances" :key="acceptance.id" class="flex items-center gap-3">
                                        <div
                                            class="flex size-10 shrink-0 items-center justify-center rounded-full body-small-bold text-valence-grey-850"
                                            :style="{ backgroundColor: CONNECTED_AVATAR_BG }">
                                            {{ getInitials(acceptance.user_name || acceptance.user_email || "") }}
                                        </div>
                                        <div class="min-w-0 flex-1">
                                            <div class="body-small-semibold text-valence-grey-850 truncate">
                                                {{ acceptance.user_name || acceptance.user_email }}
                                            </div>
                                            <div class="body-small text-valence-grey-500">Connected&nbsp;{{ timeAgo(acceptance.accepted_at) }}</div>
                                        </div>
                                        <BaseDropdownMenu :sections="acceptanceMenuSections" :side-offset="8" @select="(item) => handleAcceptanceMenuSelect(item, acceptance.id)">
                                            <template #trigger>
                                                <button type="button" class="body-small text-valence-grey-750 flex items-center gap-1 hover:text-valence-grey-850">
                                                    Connected
                                                    <i class="bi bi-chevron-down text-xs" aria-hidden="true"></i>
                                                </button>
                                            </template>
                                        </BaseDropdownMenu>
                                    </div>
                                </div>
                            </div>
                        </template>

                        <!-- Included message (organization) -->
                        <div v-if="isOrgAccessLevel" class="rounded-lgr border border-valence-grey-300 p-4 mt-3">
                            <p class="body-small-bold text-valence-grey-850">Included message</p>
                            <p class="body-small text-valence-grey-750 mt-1">{{ PROFILE_SHARE_MESSAGE }}</p>
                        </div>

                        <!-- Share actions -->
                        <div class="grid grid-cols-2 gap-3 mt-4" data-testid="share-via-section">
                            <BaseButton theme="primary" class="col-span-2" :disabled="isButtonDisabled" @click="handleCopyShareLink">
                                <i class="bi" :class="isCopied ? 'bi-check-lg' : 'bi-link-45deg'" aria-hidden="true"></i>
                                {{ isCopied ? "Copied!" : "Copy Link" }}
                            </BaseButton>

                            <BaseButton
                                v-if="orgHasSlack"
                                theme="neutral"
                                class="flex items-center justify-center rounded-lg body-small-bold"
                                :disabled="isButtonDisabled"
                                @click="handleShareViaSlack">
                                Share via Slack
                            </BaseButton>
                            <BaseButton
                                v-else-if="orgHasMsTeams"
                                theme="neutral"
                                class="flex items-center justify-center rounded-lg body-small-bold"
                                :disabled="isButtonDisabled"
                                @click="handleShareViaTeams">
                                Share via Teams
                            </BaseButton>

                            <BaseButton
                                theme="neutral"
                                class="flex items-center justify-center rounded-lg body-small-bold"
                                :class="{ 'col-span-2': !hasChatShareOption }"
                                :disabled="isButtonDisabled"
                                @click="handleShareViaEmail">
                                Share via Email
                            </BaseButton>
                        </div>
                    </template>
                    <template v-else-if="isPrivateAccessLevel">
                        <div class="flex flex-col items-center justify-center text-center rounded-lgr bg-card-surface-raised px-6 py-10 gap-3 mt-1 md:h-full">
                            <i class="bi bi-lock text-2xl text-valence-grey-750" aria-hidden="true"></i>
                            <p class="body-bold text-valence-grey-850">Your profile is private</p>
                            <p class="body-small text-valence-grey-750">
                                Only you can see it. Change the access level above to share it and help others discover how you work best.
                            </p>
                        </div>
                    </template>
                </div>
            </div>
            <p v-if="accessChangeError" class="mt-2 details text-red-600">{{ accessChangeErrorMessage }}</p>
            <p v-if="shareLinkError" class="mt-2 details text-red-600">{{ shareLinkError }}</p>
        </div>
    </BaseDialog>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { useTimeoutFn } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { ProfileAcceptanceSummary, ShareInvitationList } from "~vue/api/generated/collab-profiles";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import { useTimedShow } from "~vue/composables/useTimedShow";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import OrganizationIcon from "~vue/icons/OrganizationIcon.vue";
import type { ExistingPublicProfile, ProfilePayload } from "~vue/types/profile";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { ACCESS } from "~vue/utils/constants";
import { logUserInteraction } from "~vue/utils/logUtils";
import { getInitials } from "~vue/utils/stringUtils";
import { DateTime } from "luxon";
import { DialogClose } from "reka-ui";
import { computed, ref, watch } from "vue";

import { PROFILE_SHARE_MESSAGE, type ProfileShareTarget, useProfileShareActions } from "./composables/useProfileShareActions";

type ShareProfileDialogProps = {
    existingPublicProfile: ExistingPublicProfile;
    orgHasMsTeams?: boolean;
    orgHasSlack?: boolean;
    availableAccessLevels: Set<string>;
};

const { existingPublicProfile, orgHasMsTeams = false, orgHasSlack = false, availableAccessLevels } = defineProps<ShareProfileDialogProps>();

const profileId = computed(() => existingPublicProfile.id);

const open = defineModel<boolean>("open", { default: false });

const localAccess = ref(existingPublicProfile.access ?? ACCESS.PRIVATE);

const accessOptions = computed(() => {
    const allOptions = [
        { key: ACCESS.PRIVATE, label: "Private" },
        { key: ACCESS.INVITED_ORG_MEMBERS, label: "Invited Only" },
        { key: ACCESS.ORGANIZATION, label: "Organization" },
    ];
    return allOptions.filter((o) => availableAccessLevels.has(o.key));
});

const hasChatShareOption = computed(() => orgHasSlack || orgHasMsTeams);

const isOrgAccessLevel = computed(() => localAccess.value === ACCESS.ORGANIZATION);
const isInviteOnlyAccessLevel = computed(() => localAccess.value === ACCESS.INVITED_ORG_MEMBERS);
const isPrivateAccessLevel = computed(() => localAccess.value === ACCESS.PRIVATE);

const invitationsUrl = computed(() => (profileId.value ? `/v1/profiles/${profileId.value}/shared-invitations` : ""));

const {
    data: invitations,
    error: invitationLoadError,
    isFetching: isFetchingAcceptances,
    execute: fetchInvitations,
} = useFetch(invitationsUrl, { immediate: false, refetch: false }).json<ShareInvitationList>();

const showInvitationSkeletons = computed(() => isFetchingAcceptances.value && invitations.value === null);
const ensureInvitationsLoaded = () => {
    if (!profileId.value || isFetchingAcceptances.value) {
        return;
    }
    if (invitations.value === null || invitationLoadError.value) {
        void fetchInvitations();
    }
};

const activeAcceptances = computed<ProfileAcceptanceSummary[]>(() => invitations.value?.acceptances?.filter((a) => a.revoked_at === null) ?? []);

const revokeUrl = ref("");
// The revoke endpoint returns 204 No Content: .json() would throw on the empty body, and useFetch
// parses before checking response.ok, so every successful revoke logged an error to Sentry.
const { execute: executeRevoke } = useFetch(revokeUrl, { immediate: false }).delete();

const acceptanceMenuSections: DropdownMenuSection[] = [{ id: "access", items: [{ id: "revoke", type: "button", label: "Remove access", theme: "danger" }] }];

const handleAcceptanceMenuSelect = (item: DropdownMenuItem, acceptanceId: string) => {
    if (item.id === "revoke") {
        void handleRevoke(acceptanceId);
    }
};

const handleRevoke = async (acceptanceId: string) => {
    if (!profileId.value || !isInviteOnlyAccessLevel.value) {
        return;
    }
    revokeUrl.value = `/v1/profiles/${profileId.value}/acceptances/${acceptanceId}`;
    await executeRevoke();
    await fetchInvitations();
};

const invitationShareUrl = computed(() => invitations.value?.active_link?.share_url);

const { isGeneratingLink, shareLinkError, isCopied, copyLink, shareViaSlack, shareViaTeams, shareViaEmail } = useProfileShareActions({
    profileId,
    access: localAccess,
    activeShareUrl: invitationShareUrl,
});

const trackShare = (target: ProfileShareTarget) => {
    void logUserInteraction(AmplitudeEventNames.PUBLIC_PROFILE_SHARED, { target, access: localAccess.value });
};

const handleCopyShareLink = async () => {
    if (await copyLink()) {
        trackShare("link");
    }
};

const handleShareViaSlack = async () => {
    if (await shareViaSlack()) {
        trackShare("slack");
    }
};

const handleShareViaTeams = async () => {
    if (await shareViaTeams()) {
        trackShare("teams");
    }
};

const handleShareViaEmail = async () => {
    if (await shareViaEmail()) {
        trackShare("email");
    }
};

const CONNECTED_AVATAR_BG = "#9EDE91";

const timeAgo = (dateStr: string): string => {
    return DateTime.fromISO(dateStr).toRelative() ?? "just now";
};

const profileAccessPayload = ref<ProfilePayload | null>(null);
const {
    execute: executeToggleAccess,
    error: accessChangeError,
    isFetching: isAccessChanging,
    statusCode: accessChangeStatus,
} = useFetch("/api/accounts/profile/", { immediate: false }).post(profileAccessPayload).json();

const accessChangeErrorMessage = computed(() =>
    accessChangeStatus.value === 429 ? "Access change failed, please wait a moment and try again." : "Access change failed, please try again.",
);

const ACCESS_ERROR_TIMEOUT_MS = 5000;
const { start: startAccessErrorTimeout, stop: stopAccessErrorTimeout } = useTimeoutFn(
    () => {
        accessChangeError.value = null;
    },
    ACCESS_ERROR_TIMEOUT_MS,
    { immediate: false },
);

watch(accessChangeError, (error) => {
    if (error) {
        startAccessErrorTimeout();
    } else {
        stopAccessErrorTimeout();
    }
});

watch([open, isInviteOnlyAccessLevel], ([isOpen, isInviteOnly]) => {
    if (isOpen && isInviteOnly) {
        ensureInvitationsLoaded();
    } else if (!isOpen) {
        invitations.value = null;
        invitationLoadError.value = null;
        accessChangeError.value = null;
    }
});

async function handleChangeProfileAccess(newShared: string): Promise<void> {
    if (!existingPublicProfile || existingPublicProfile.access === newShared) {
        return;
    }
    hideAccepted();
    accessChangeError.value = null;

    profileAccessPayload.value = {
        name: existingPublicProfile.name ?? "",
        title: existingPublicProfile.title ?? "",
        division: existingPublicProfile.division ?? "",
        is_manager: existingPublicProfile.is_manager ?? false,
        access: newShared,
    };

    await executeToggleAccess();

    if (accessChangeError.value) {
        localAccess.value = existingPublicProfile.access ?? ACCESS.PRIVATE;
    } else {
        void logUserInteraction(AmplitudeEventNames.PUBLIC_PROFILE_VISIBILITY_CHANGED, {
            new_access: newShared,
            is_shared: newShared === ACCESS.ORGANIZATION,
        });
        showAccepted();
        router.reload({ only: ["existing_public_profile"] });
    }
}

const isButtonDisabled = computed(() => isGeneratingLink.value);

const { isShowing: isShowingAccepted, show: showAccepted, hide: hideAccepted } = useTimedShow();
</script>
